/**
 * Office layout — what its regions share: the accent as a FILL, the hover /
 * focus rules inline styles cannot express, a tooltip that also shows on
 * focus, and two small hooks.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { S } from '../../nav/tokens'

/**
 * The accent as a fill under WHITE text (the Menu button, the Menu panel's
 * section column). The dark theme's accent (#4c8dff) is a TEXT colour — white
 * on it is about 3.3:1 — so a filled surface darkens it until white passes AA
 * (≥ 4.5:1) in both themes. Accent TEXT on a panel uses `S.accent` itself.
 */
export const ACCENT_SOLID = `color-mix(in srgb, ${S.accent} 76%, #000)`

/** Class-based hover / focus for this layout only (mounted by the layout). An
 *  element marked current / selected keeps its own highlight on hover. */
export const OFFICE_CSS = `
.tw-oc-rb{transition:background-color .12s,border-color .12s}
.tw-oc-rb:not([aria-current="page"]):not([aria-expanded="true"]):not(:disabled):hover{background:var(--color-surface-2,#f0f3f7)!important}
.tw-oc-rb:focus-visible,.tw-oc-tab:focus-visible,.tw-oc-sheet-tab:focus-visible,.tw-oc-plain:focus-visible{outline:2px solid var(--color-accent,#0061D0);outline-offset:-2px}
.tw-oc-tab:not([aria-selected="true"]):hover{background:var(--color-surface-2,#f0f3f7)}
.tw-oc-sheet:not([data-selected="true"]):hover{background:var(--color-bg-secondary,#ffffff)}
.tw-oc-plain:not(:disabled):hover{background:var(--color-surface-2,#f0f3f7);color:var(--color-text,#1c2430)}
.tw-oc-solid:hover{filter:brightness(1.12)}
.tw-oc-solid:focus-visible{outline:2px solid var(--color-text,#1c2430);outline-offset:2px}
.tw-oc-bs-item:hover{background:rgba(0,0,0,0.16)}
.tw-oc-bs button:focus-visible{outline:2px solid #ffffff;outline-offset:-3px}
.tw-oc-bs-pane button:focus-visible{outline:2px solid var(--color-accent,#0061D0);outline-offset:-2px}
.tw-oc-label{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;word-break:break-word}
@media (prefers-reduced-motion:reduce){.tw-oc-rb{transition:none}}
`

/** Visually hidden, still read (the screen's heading: the path bar SHOWS the title as its last crumb). */
export const SR_ONLY: CSSProperties = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden',
  clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
}

/**
 * A tooltip for an icon-only control that shows on hover AND keyboard focus
 * (the shell's `.tw-sh-info` / `.tw-sh-tip` rule) — a native `title` never
 * shows on focus.
 */
export function IconTip({ label, children, place = 'below', align = 'left' }: {
  label: string; children: ReactNode; place?: 'below' | 'above'; align?: 'left' | 'right'
}) {
  return (
    <span className="tw-sh-info" style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      {children}
      <span className="tw-sh-tip" role="tooltip" style={{
        position: 'absolute', [align]: 0, zIndex: 9450, pointerEvents: 'none',
        ...(place === 'below' ? { top: 'calc(100% + 6px)' } : { bottom: 'calc(100% + 6px)' }),
        whiteSpace: 'nowrap', padding: '6px 10px', borderRadius: 6,
        background: S.text, color: S.panel, fontSize: 13, lineHeight: 1.45, fontWeight: 400,
        boxShadow: '0 6px 18px rgba(0,0,0,0.22)',
      } as CSSProperties}>
        {label}
      </span>
    </span>
  )
}

/** A boolean view preference kept in localStorage (a convenience only — the
 *  layout renders correctly when storage is unavailable). */
export function usePersistedFlag(key: string, initial: boolean): [boolean, (on: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(key)
      return raw === null ? initial : raw === '1'
    } catch { return initial }
  })
  const set = useCallback((on: boolean) => {
    setValue(on)
    try { localStorage.setItem(key, on ? '1' : '0') } catch { /* storage unavailable: view state only */ }
  }, [key])
  return [value, set]
}

/** Close a pop-up on a mouse-down outside it. `inside` decides what counts as
 *  inside (the pop-up AND the control that opened it, so its own click toggles). */
export function useOutsideDown(open: boolean, inside: (target: Node) => boolean, onOutside: () => void) {
  const insideRef = useRef(inside)
  insideRef.current = inside
  const outsideRef = useRef(onOutside)
  outsideRef.current = onOutside
  useEffect(() => {
    if (!open) return undefined
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null
      if (t && insideRef.current(t)) return
      outsideRef.current()
    }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [open])
}

/** Tabbable elements inside `root`, in DOM order (a dialog's focus trap). */
export function tabbables(root: HTMLElement | null): HTMLElement[] {
  if (!root) return []
  const sel = 'button, [href], input, select, textarea, [tabindex]'
  return [...root.querySelectorAll<HTMLElement>(sel)].filter((el) => {
    if ((el as HTMLButtonElement).disabled) return false
    if (el.tabIndex < 0) return false
    return el.offsetParent !== null || el === document.activeElement
  })
}

/** Move focus among `els` for Left/Right (or Up/Down) + Home/End. Returns the
 *  element focused, or null when the key is not a move. */
export function moveFocus(els: HTMLElement[], key: string, horizontal = true): HTMLElement | null {
  const i = els.indexOf(document.activeElement as HTMLElement)
  if (i < 0 || !els.length) return null
  const fwd = horizontal ? 'ArrowRight' : 'ArrowDown'
  const back = horizontal ? 'ArrowLeft' : 'ArrowUp'
  const j = key === fwd ? i + 1 : key === back ? i - 1 : key === 'Home' ? 0 : key === 'End' ? els.length - 1 : null
  if (j === null) return null
  const el = els[(j + els.length) % els.length]
  el?.focus()
  return el || null
}
